import React, { useState, useEffect } from 'react';
import { X, Lightbulb, Sparkle, Spinner, Lightning, Copy, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { growthAPI } from '@/utils/api';
import WhyThisFitsYouBlock from './WhyThisFitsYouBlock';
import { getUser } from '@/utils/auth';
import BusinessMemoryBanner from './BusinessMemoryBanner';

const ViralIdeasModal = ({ isOpen, onClose, onSelectIdea, onSelectIdeaForPack, onSuccess }) => {
  const [step, setStep] = useState('loading'); // 'loading', 'results'
  const [ideas, setIdeas] = useState([]);
  const [niche, setNiche] = useState('');
  const [platform, setPlatform] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const user = getUser();

  useEffect(() => {
    if (isOpen) {
      fetchViralIdeas();
    }
  }, [isOpen]);

  const fetchViralIdeas = async () => {
    setStep('loading');
    try {
      const response = await growthAPI.getViralIdeas({
        niche: user?.niche,
        platform: user?.platform,
        count: 15
      });
      setIdeas(response.data.ideas || []);
      setNiche(response.data.niche);
      setPlatform(response.data.platform);
      setStep('results');
      // Round 218D P0 (2026-02-07) — fires the resolver-driven
      // Continue dialog. The user counter viral_ideas_generated_count
      // has been bumped server-side by /growth/viral-ideas so the
      // resolver now advances to the "content" tail step.
      try { onSuccess?.({ meta: { title: 'Viral ideas ready.' } }); } catch (_) { /* noop */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate ideas');
      onClose();
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUseIdea = (idea) => {
    if (onSelectIdea) {
      // Pass the FULL idea object (title, hook, angle, platform, format, ...)
      // so Create Content can prefill more than just the topic and show a
      // "Creating from selected viral idea" banner.
      onSelectIdea({
        topic: idea.title || idea.topic || '',
        title: idea.title,
        hook: idea.hook,
        angle: idea.content_angle || idea.angle,
        platform: idea.platform,
        format: idea.format,
        difficulty: idea.difficulty,
        id: idea.id,
      });
    }
    toast.success('Idea selected! Opening content generator...');
    onClose();
  };

  // Parallel handoff: send the same rich idea object into Content Pack.
  // Uses the existing selectedIdea plumbing in DashboardPage → ContentPackModal
  // so business memory still wins for blank fields.
  const handleUseIdeaForPack = (idea) => {
    if (onSelectIdeaForPack) {
      onSelectIdeaForPack({
        topic: idea.title || idea.topic || '',
        title: idea.title,
        hook: idea.hook,
        angle: idea.content_angle || idea.angle,
        platform: idea.platform,
        format: idea.format,
        difficulty: idea.difficulty,
        id: idea.id,
      });
    }
    toast.success('Idea selected! Opening Content Pack...');
    onClose();
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'bg-emerald-500/20 text-emerald-400';
      case 'medium': return 'bg-amber-500/20 text-amber-400';
      case 'hard': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-3xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Lightbulb size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Find Viral Ideas</h2>
                <p className="text-sm text-slate-400">15 trending ideas for {niche || 'your niche'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Loading State */}
            {step === 'loading' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Discovering Viral Ideas</h3>
                <p className="text-slate-400">Analyzing trends in {user?.niche || 'your niche'}...</p>
              </div>
            )}

            {/* Results */}
            {step === 'results' && (
              <div className="space-y-4">
                <BusinessMemoryBanner />
                {/* Header Info */}
                <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Sparkle size={20} className="text-yellow-400" weight="fill" />
                    <span className="text-yellow-400 font-medium">Pro Tip</span>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Click "Use This" to start creating content with any idea. Each hook is optimized for {platform || 'social media'} engagement.
                  </p>
                </div>

                {/* Round 92 — Why This Fits You + feedback */}
                <WhyThisFitsYouBlock
                  department="growth"
                  taskType="growth_strategy"
                  recommendationId={ideas?.[0]?.batch_id || (ideas?.length ? `viral-ideas-${platform || 'default'}-${ideas.length}` : null)}
                  recommendationSummary={ideas?.length ? `${ideas.length} viral ideas tuned for ${platform || 'your platform'}` : null}
                  businessCtx={{ platform }}
                  className="mb-4"
                />

                {/* Ideas List */}
                <div className="space-y-3">
                  {ideas.map((idea, index) => (
                    <motion.div
                      key={idea.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all group"
                      data-testid={`viral-idea-${index}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-500 font-mono">#{index + 1}</span>
                            <h4 className="text-white font-semibold truncate">{idea.title}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getDifficultyColor(idea.difficulty)}`}>
                              {idea.difficulty}
                            </span>
                          </div>
                          
                          {/* Hook */}
                          <p className="text-yellow-400 text-sm mb-2 italic">
                            "{idea.hook}"
                          </p>
                          
                          {/* Content Angle */}
                          <p className="text-slate-400 text-sm">
                            {idea.content_angle}
                          </p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleUseIdea(idea)}
                            className="px-3 py-1.5 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-1"
                          >
                            <ArrowRight size={14} />
                            <span>Use This</span>
                          </button>
                          <button
                            onClick={() => handleUseIdeaForPack(idea)}
                            data-testid={`viral-idea-pack-${index}`}
                            className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-1 whitespace-nowrap"
                          >
                            <Sparkle size={14} weight="fill" />
                            <span>Create Content Pack</span>
                          </button>
                          <button
                            onClick={() => handleCopy(`${idea.title}\n\nHook: ${idea.hook}\n\n${idea.content_angle}`, idea.id)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-1 ${
                              copiedId === idea.id
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-white/10 hover:bg-white/20 text-white'
                            }`}
                          >
                            {copiedId === idea.id ? (
                              <>
                                <CheckCircle size={14} />
                                <span>Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy size={14} />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Refresh Button */}
                <div className="pt-4 flex justify-center">
                  <button
                    onClick={fetchViralIdeas}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
                  >
                    <Lightning size={18} weight="fill" />
                    <span>Generate More Ideas</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ViralIdeasModal;
